import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabaseClient";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useViewAsClient } from "@/lib/viewAsClient";
import ToSAcceptanceGate from "@/components/ToSAcceptanceGate";
import OutboxHealthPill from "@/components/OutboxHealthPill";
import EmailAlertsBell from "@/components/EmailAlertsBell";
import {
  Archive,
  BarChart3,
  Calendar,
  ChevronDown,
  ClipboardCheck,
  FileText,
  LayoutGrid,
  LogOut,
  Mail,
  Megaphone,
  Palette,
  Tag,
  Settings,
  HelpCircle,
  ShieldCheck,
  ClipboardList as ClipboardListIcon,
  Truck,
  Users,
  Warehouse,
  ClipboardList,
  Briefcase,
  Beaker,
  Package,
  AlertTriangle,
  BookOpen,
  Building2,
  Globe,
  Repeat,
  Ban,
  Plus,
  Boxes,
} from "lucide-react";

// ── Top row: daily operational items ────────────────────────────────
const TOP_NAV = [
  { to: "/",                      label: "Dashboard",      icon: LayoutGrid,    roles: null },
  { to: "/dock",                  label: "Dock manager",   icon: Warehouse,     roles: ["super_admin", "client_admin", "site_admin", "dock_manager", "receiving", "dispatch", "gate"] },
  { to: "/site-manager",          label: "Site manager",   icon: Calendar,      roles: ["super_admin", "client_admin", "site_admin", "dock_manager"] },
  { to: "/bay-view",              label: "Bay view",       icon: Boxes,         roles: ["super_admin", "client_admin", "site_admin", "dock_manager", "receiving", "dispatch", "gate"] },
  { to: "/bookings",              label: "Bookings",       icon: ClipboardList, roles: null },
  { to: "/bookings/new",          label: "New booking",    icon: Plus,          roles: ["super_admin", "client_admin", "site_admin", "dock_manager", "dispatch", "receiving", "approved_booker"] },
  { to: "/booker",                label: "Booker",         icon: Briefcase,     roles: ["approved_booker"] },
  { to: "/drivers",               label: "My drivers",     icon: Users,         roles: ["approved_booker"] },
  { to: "/admin/drivers",         label: "Drivers",        icon: Truck,         roles: ["super_admin", "client_admin", "site_admin", "dock_manager", "receiving", "dispatch", "gate"] },
  { to: "/broker",                label: "My submissions", icon: Briefcase,     roles: ["broker"] },
  { to: "/driver",                label: "My jobs",        icon: ClipboardList, roles: ["driver"] },
];

// ── Bottom row: grouped dropdowns (admin / configuration) ───────────
const NAV_GROUPS = [
  {
    label: "Pallets",
    roles: ["super_admin", "client_admin", "site_admin", "dock_manager"],
    items: [
      { to: "/admin/pallets",          label: "Reconciliation",     icon: Package,    roles: ["super_admin", "client_admin", "site_admin", "dock_manager"] },
      { to: "/admin/pallets/hire",     label: "Hire / dehire",      icon: ClipboardCheck, roles: ["super_admin", "client_admin", "site_admin", "dock_manager"] },
    ],
  },
  {
    label: "QA",
    roles: ["super_admin", "client_admin", "site_admin", "dock_manager"],
    items: [
      { to: "/admin/issues",           label: "NCR inbox",          icon: AlertTriangle, roles: ["super_admin", "client_admin", "site_admin", "dock_manager"] },
      { to: "/admin/sampling-rules",   label: "Sampling rules",     icon: Beaker,        roles: ["super_admin", "client_admin", "site_admin"] },
      { to: "/admin/sampling-queue",   label: "Sample queue",       icon: Beaker,        roles: ["super_admin", "client_admin", "site_admin", "dock_manager"] },
    ],
  },
  {
    label: "Reports",
    roles: ["super_admin", "client_admin", "site_admin"],
    items: [
      { to: "/admin/reports",          label: "All reports",        icon: BarChart3, roles: ["super_admin", "client_admin", "site_admin"] },
      { to: "/admin/reports/custom",   label: "Custom builder",     icon: FileText,  roles: ["super_admin", "client_admin", "site_admin"] },
    ],
  },
  {
    label: "Sites",
    roles: ["super_admin", "client_admin", "site_admin", "dock_manager"],
    items: [
      { to: "/sites",              label: "Sites & bays",  icon: Building2, roles: ["super_admin", "client_admin", "site_admin"] },
      { to: "/recurring",          label: "Recurring",     icon: Repeat,    roles: ["super_admin", "client_admin", "site_admin", "dock_manager"] },
      { to: "/blackouts",          label: "Blackouts",     icon: Ban,       roles: ["super_admin", "client_admin", "site_admin", "dock_manager"] },
      { to: "/admin/public-links", label: "Public links",  icon: Globe,     roles: ["super_admin", "client_admin"] },
    ],
  },
  {
    label: "Administration",
    roles: ["super_admin", "client_admin"],
    items: [
      { to: "/users",                     label: "Users",            icon: Users,     roles: ["super_admin", "client_admin"] },
      { to: "/admin/approved-bookers",    label: "Approved Bookers", icon: Briefcase, roles: ["super_admin", "client_admin"] },
      { to: "/admin/onboarding",          label: "Driver onboarding", icon: Truck,    roles: ["super_admin", "client_admin"] },
      { to: "/admin/email",               label: "Email settings",   icon: Mail,          roles: ["super_admin", "client_admin"] },
      { to: "/admin/email-log",           label: "Email log",        icon: ClipboardList, roles: ["super_admin", "client_admin"] },
      { to: "/admin/email-templates",     label: "Email templates",  icon: Mail,          roles: ["super_admin", "client_admin"] },
      { to: "/admin/broadcasts",          label: "Broadcasts",       icon: Megaphone,     roles: ["super_admin", "client_admin", "site_admin"] },
      { to: "/admin/override-requests",   label: "Override requests", icon: ShieldCheck,  roles: ["super_admin", "client_admin", "site_admin", "dock_manager"] },
      { to: "/admin/branding",            label: "Branding",         icon: Palette,       roles: ["super_admin", "client_admin"] },
      { to: "/admin/reservation-types",   label: "Reservation types", icon: Tag,          roles: ["super_admin", "client_admin"] },
      { to: "/admin/terms-of-service",    label: "Terms of Service", icon: FileText,      roles: ["super_admin", "client_admin"] },
    ],
  },
  {
    label: "Preferences",
    roles: ["super_admin", "client_admin", "site_admin"],
    items: [
      { to: "/admin/clients",         label: "Client settings", icon: Archive,      roles: ["super_admin", "client_admin"] },
      { to: "/admin/picklists",       label: "Picklists",       icon: ShieldCheck,  roles: ["super_admin", "client_admin"] },
      { to: "/admin/form-designer",   label: "Form designer",    icon: LayoutGrid,   roles: ["super_admin", "client_admin", "site_admin"] },
      { to: "/admin/driver-declarations", label: "Driver declarations", icon: ClipboardListIcon, roles: ["super_admin", "client_admin"] },
      { to: "/admin/field-catalogue", label: "Field catalogue",  icon: BookOpen,     roles: ["super_admin", "client_admin", "site_admin"] },
      { to: "/admin/email-handling",  label: "Email handling",  icon: Mail,         roles: ["super_admin", "client_admin", "site_admin"] },
      { to: "/admin/booking-edit",    label: "Booking edit",    icon: LayoutGrid,   roles: ["super_admin", "client_admin", "site_admin"] },
      { to: "/admin/archive",         label: "Archive browser", icon: Archive,      roles: ["super_admin", "client_admin", "site_admin"] },
    ],
  },
  {
    label: "Import",
    roles: ["super_admin", "client_admin"],
    items: [
      { to: "/admin/import/users",     label: "Users CSV",     icon: Users,       roles: ["super_admin", "client_admin"] },
      { to: "/admin/import/bookings",  label: "Bookings CSV",  icon: ClipboardList, roles: ["super_admin", "client_admin"] },
      { to: "/admin/import/approved-bookers", label: "Carriers CSV", icon: Briefcase, roles: ["super_admin", "client_admin"] },
      { to: "/admin/import/picklists", label: "Picklists CSV", icon: ShieldCheck, roles: ["super_admin", "client_admin"] },
      { to: "/admin/import/archive",   label: "Archive CSV",   icon: Archive,     roles: ["super_admin", "client_admin"] },
    ],
  },
];

// Roles that get the shrunken nav (no bottom row, no View-As)
const SHRUNKEN_NAV_ROLES = new Set(["approved_booker", "driver"]);

export default function AppLayout() {
  const { user, profile, roles, hasRole, signOut, assignments } = useAuth();
  const { activeClient } = useViewAsClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Branding source:
  //  - super_admin "viewing as" a tenant → that tenant's brand
  //  - everyone else → their first assigned client's brand
  const [brandClient, setBrandClient] = useState(null);
  const [myCompanies, setMyCompanies] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (activeClient) {
        setBrandClient(activeClient);
        setMyCompanies([]);
        return;
      }
      const ids = [...new Set(assignments.map((a) => a.client_id).filter(Boolean))];
      if (!ids.length) {
        setBrandClient(null);
        return;
      }
      const { data } = await supabase
        .from("lbs_clients")
        .select("id,name,slug,brand_logo_url,brand_primary_color")
        .in("id", ids);
      if (cancelled) return;
      // Several companies (e.g. a driver for KCS + Cheetham Salt): no single
      // tenant brand — show them all and label each job with its company.
      const list = (data || []).sort((a, b) => a.name.localeCompare(b.name));
      setMyCompanies(list.length > 1 ? list : []);
      setBrandClient(list.length === 1 ? list[0] : null);
    })();
    return () => { cancelled = true; };
  }, [activeClient, assignments]);

  // Apply the brand primary colour as a CSS variable on document root.
  useEffect(() => {
    const color = brandClient?.brand_primary_color;
    if (color && /^#([0-9a-fA-F]{6})$/.test(color)) {
      document.documentElement.style.setProperty("--brand-primary", color);
    } else {
      document.documentElement.style.removeProperty("--brand-primary");
    }
  }, [brandClient?.brand_primary_color]);

  const initials =
    (profile?.first_name?.[0] || user?.email?.[0] || "?").toUpperCase() +
    (profile?.last_name?.[0] || "").toUpperCase();

  const displayName =
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || user?.email;

  const visibleTop = TOP_NAV.filter((it) => !it.roles || hasRole(it.roles));

  // Phase E: shrunken nav for approved_booker / driver roles — hide groups + View-As.
  // Internal admin roles (super_admin, client_admin, site_admin etc.) take precedence
  // so admins who *also* happen to hold a driver/booker role keep the full nav.
  const internalRoles = ["super_admin", "client_admin", "site_admin", "dock_manager",
                         "receiving", "dispatch", "gate"];
  const hasInternal = roles.some((r) => internalRoles.includes(r));
  const isShrunken = !hasInternal && roles.some((r) => SHRUNKEN_NAV_ROLES.has(r));

  const visibleGroups = isShrunken
    ? []
    : NAV_GROUPS
        .map((g) => ({ ...g, items: g.items.filter((it) => !it.roles || hasRole(it.roles)) }))
        .filter((g) => g.items.length > 0 && (!g.roles || hasRole(g.roles)));

  // Flat list for mobile (single horizontal scroll)
  const mobileNav = [
    ...visibleTop,
    ...visibleGroups.flatMap((g) => g.items),
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <ToSAcceptanceGate user={user} brandClient={brandClient} />
      {/* Top bar */}
      <header
        data-testid="app-header"
        className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60"
      >
        {/* Row 1: brand, top-nav, user controls (balance line) */}
        <div className="mx-auto flex h-14 max-w-screen-2xl items-center gap-4 px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            {brandClient?.brand_logo_url ? (
              <img
                src={brandClient.brand_logo_url}
                alt={`${brandClient.name} logo`}
                className="h-7 w-7 rounded object-contain bg-white border border-slate-200"
                data-testid="tenant-logo"
              />
            ) : (
              <div className="rounded-lg bg-blue-600 p-1.5">
                <Truck className="h-4 w-4 text-white" strokeWidth={2} />
              </div>
            )}
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-tight">timeslot.au</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 max-w-[260px] truncate"
                   data-testid="header-tenant-label"
                   title={myCompanies.length ? myCompanies.map((c) => c.name).join(", ") : undefined}>
                {myCompanies.length
                  ? `${isShrunken && roles.includes("driver") ? "Driver · " : ""}${myCompanies.map((c) => c.name).join(", ")}`
                  : (brandClient?.name || "LBS")}
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1 text-sm" data-testid="nav-top">
            {visibleTop.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                onClick={() => {
                  // Remember where the user was BEFORE they kicked off the
                  // new-booking funnel, so /bookings/new/edit can send them
                  // back to that page after save. Only set when leaving
                  // a page OTHER than /bookings/new/* itself.
                  if (to === "/bookings/new" && !location.pathname.startsWith("/bookings/new")) {
                    try {
                      sessionStorage.setItem(
                        "lbs.bookingReturnTo",
                        location.pathname + location.search,
                      );
                    } catch (_e) { /* sessionStorage disabled */ }
                  }
                }}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 transition ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
                data-testid={`nav-${to.replace(/^\//, "").replace(/\//g, "-") || "home"}`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2 sm:gap-3 min-w-0">
            {!isShrunken && <ViewAsClientPicker />}
            {(roles.includes("super_admin") || roles.includes("client_admin")) && <OutboxHealthPill />}
            {(roles.includes("super_admin") || roles.includes("client_admin")) && <EmailAlertsBell />}
            {roles.length > 0 && (
              <Badge variant="secondary" className="hidden sm:inline-flex whitespace-nowrap" data-testid="role-badge">
                {roles[0].replace("_", " ")}
              </Badge>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="rounded-full ring-offset-2 focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-slate-200 text-xs">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm">{displayName}</div>
                  <div className="text-xs text-slate-500">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <Settings className="mr-2 h-4 w-4" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/help/booking-flow")} data-testid="menu-help-booking-flow">
                  <HelpCircle className="mr-2 h-4 w-4" /> Booking flow guide
                </DropdownMenuItem>
                {(hasRole(["super_admin"]) || brandClient?.slug === "kcs") && (
                  <DropdownMenuItem onClick={() => navigate("/help/user-manual")} data-testid="menu-user-manual">
                    <HelpCircle className="mr-2 h-4 w-4" /> User manual
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} data-testid="menu-signout">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Row 2: grouped dropdowns (admin / configuration) */}
        {visibleGroups.length > 0 && (
          <div
            className="hidden md:flex mx-auto h-10 max-w-screen-2xl items-center gap-1 border-t border-slate-100 px-4 sm:px-6"
            data-testid="nav-groups"
          >
            {visibleGroups.map((g) => {
              const isActive = g.items.some((it) => location.pathname === it.to);
              return (
                <DropdownMenu key={g.label}>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`nav-group-${g.label.toLowerCase()}`}
                      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition ${
                        isActive
                          ? "bg-slate-100 text-slate-900"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      }`}
                    >
                      {g.label}
                      <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {g.items.map(({ to, label, icon: Icon }) => (
                      <DropdownMenuItem
                        key={to}
                        onClick={() => navigate(to)}
                        data-testid={`nav-${to.replace(/^\//, "").replace(/\//g, "-")}`}
                        className={location.pathname === to ? "bg-slate-100 font-medium" : ""}
                      >
                        <Icon className="mr-2 h-4 w-4" />
                        {label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
          </div>
        )}

        {/* Mobile nav (flat horizontal scroll) */}
        <nav className="flex md:hidden gap-1 overflow-x-auto border-t px-4 py-2">
          {mobileNav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1 text-xs ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-600"
                }`
              }
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-screen-2xl px-4 py-6 sm:px-6">
        <ViewAsClientBanner />
        <AppBroadcastBanners />
        <Outlet />
      </main>
    </div>
  );
}

/**
 * Active broadcasts (banner channel) shown at the top of the admin app.
 * Pulls every active banner-channel broadcast for the current tenant
 * (super-admin → viewAsClientId, otherwise the user's own client) so
 * admins always see what they pushed without having to open a site page.
 */
function AppBroadcastBanners() {
  const { viewAsClientId } = useViewAsClient();
  const { assignments = [], hasRole } = useAuth();
  const isSuper = hasRole(["super_admin"]);
  const cids = isSuper
    ? (viewAsClientId ? [viewAsClientId] : [])
    : [...new Set(assignments.map((a) => a.client_id).filter(Boolean))];
  const cidKey = cids.join(",");
  const { pathname } = useLocation();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener("lbs:broadcasts-changed", bump);
    return () => window.removeEventListener("lbs:broadcasts-changed", bump);
  }, []);
  const [rows, setRows] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("lbs.broadcastsDismissed") || "[]"); }
    catch (_e) { return []; }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cidKey) { if (!cancelled) setRows([]); return; }
      const { data } = await supabase
        .from("lbs_broadcasts")
        .select("*")
        .in("client_id", cidKey.split(","))
        .order("created_at", { ascending: false })
        .limit(50);
      if (cancelled) return;
      const now = Date.now();
      // Site-specific banners: only for users of that site (or tenant-wide users).
      const seesSite = (b) => isSuper || !b.site_id || assignments.some(
        (a) => a.client_id === b.client_id && (!a.site_id || a.site_id === b.site_id));
      setRows((data || []).filter(
        (b) => b.channels?.banner && b.banner_enabled !== false && seesSite(b) &&
               (!b.banner_expires_at || new Date(b.banner_expires_at).getTime() > now)
      ));
    })();
    return () => { cancelled = true; };
  }, [cidKey, isSuper, assignments, pathname, tick]);

  const visible = rows.filter((r) => !dismissed.includes(r.id));
  if (visible.length === 0) return null;

  const close = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    try { localStorage.setItem("lbs.broadcastsDismissed", JSON.stringify(next)); }
    catch (_e) { /* storage unavailable */ }
  };

  return (
    <div className="space-y-2 mb-4" data-testid="app-broadcast-banners">
      {visible.map((b) => (
        <div key={b.id} className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex gap-3"
             data-testid={`app-broadcast-${b.id}`}>
          <Megaphone className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-900">{b.title}</div>
            <div className="text-sm text-amber-900/90 whitespace-pre-wrap">{b.body_markdown}</div>
            {b.banner_expires_at && (
              <div className="text-[11px] text-amber-700 mt-1">
                Active until {new Date(b.banner_expires_at).toLocaleString("en-AU", { hourCycle: "h23" })}
              </div>
            )}
          </div>
          <button onClick={() => close(b.id)}
                  className="text-amber-700/70 hover:text-amber-900"
                  data-testid={`app-broadcast-dismiss-${b.id}`} aria-label="Dismiss">
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Super-admin client switcher (top nav) ──────────────────────────────
function ViewAsClientPicker() {
  const { isSuperAdmin, clients, viewAsClientId, setViewAsClientId } = useViewAsClient();
  if (!isSuperAdmin || (clients || []).length === 0) return null;
  return (
    <select
      value={viewAsClientId}
      onChange={(e) => setViewAsClientId(e.target.value)}
      className="text-xs border rounded-md px-2 py-1 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 max-w-[120px] sm:max-w-none"
      data-testid="view-as-client-picker"
      title="View as client (super-admin only)"
    >
      <option value="">All tenants</option>
      {clients.map((c) => (
        <option key={c.id} value={c.id}>{`View as ${c.name}`}</option>
      ))}
    </select>
  );
}

function ViewAsClientBanner() {
  const { activeClient, setViewAsClientId, isSuperAdmin } = useViewAsClient();
  if (!isSuperAdmin || !activeClient) return null;
  return (
    <div
      data-testid="view-as-client-banner"
      className="mb-4 flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
    >
      <div>
        <strong>Viewing as {activeClient.name}</strong> — UI filters apply to this tenant.
      </div>
      <button
        className="underline hover:text-amber-900"
        onClick={() => setViewAsClientId("")}
        data-testid="view-as-clear"
      >
        Clear &amp; show all
      </button>
    </div>
  );
}
